import { isString } from '@develop/ia-utils'
import type { ParamValueZeroOrMore } from 'unplugin-vue-router/types'
import { replaceUmlaut } from '.'
import type { EnsuredPharmacyParams, PartialPharmacyParams, PharmacyParams, ProductParams } from '~/models/middleware-types'
import type { RouteLocation, RouteLocationRaw } from '#vue-router'

export const validateReturnUrl = (url?: string): string => {
  if (!url) {
    return '/'
  }
  const regex = /^https?:\/\/([a-zA-Z0-9.-]*ihreapotheken\.de|localhost:\d+)/
  return regex.test(url) ? url : '/'
}

// TODO: replace with export from ia-utils
export const isAbsoluteUrl = (url: string) => (/^https?:\/\//).test(url)

export const isExternalURL = (url: string) => new URL(url)?.origin !== location.origin

export const isExternalPath = (to: RouteLocation | undefined | null) => {
  if (!to)
    return

  if ('path' in to) {
    const { path } = to
    return isAbsoluteUrl(path)
  }

  return false
}

export const routeResolver = (to: RouteLocationRaw | undefined | null) => {
  const router = useRouter()

  if (!to)
    return

  if (isString(to)) {
    if (isAbsoluteUrl(to)) {
      const url = new URL(to)
      return router.resolve(url.pathname).name
    }

    return router.resolve(to).name
  }

  return 'path' in to ? router.resolve(to.path).name : to.name
}

export const ensurePharmacyParamsType = <T extends (Partial<PharmacyParams> | ProductParams)>(params: T) => {
  return params as unknown as Omit<typeof params, 'pharmacyName'> & { pharmacyName: ParamValueZeroOrMore<true> }
}

export const nameToSlug = (value: string) => {
  const convertedValue = replaceUmlaut(value)
  return convertedValue
    .toLowerCase()
    .replace(/\//g, '-')
    .replace(/#|_|\+|\/|%|\.|\,|\:|\;|\?|‒/g, '')
    .replace(/\s/g, '-')
}

export function convertPharmacySlugToParams(slug: string, opts: { ensureRouteParamsType: true }): EnsuredPharmacyParams | undefined
export function convertPharmacySlugToParams(slug: string, opts?: { ensureRouteParamsType?: false }): PartialPharmacyParams | undefined
export function convertPharmacySlugToParams(slug: string, opts?: { ensureRouteParamsType?: boolean }): PartialPharmacyParams | EnsuredPharmacyParams | undefined {
  if (!slug) {
    return
  }

  // Full slug: {name}-{plz}-{id}
  const fullSlugRegex = /^(.+)-(\d+)-(\d+)$/
  // Partial slug: {name}-{id}
  const nameAndIdSlugRegex = /^(.+)-(\d+)$/
  // Id-only slug: {id}
  const idOnlySlugRegex = /^(\d+)$/

  let params: PartialPharmacyParams | undefined

  const fullMatch = slug.match(fullSlugRegex)
  const nameAndIdMatch = slug.match(nameAndIdSlugRegex)
  const idOnlyMatch = slug.match(idOnlySlugRegex)

  if (fullMatch) {
    const [, pharmacyName, pharmacyPlz, pharmacyId] = fullMatch
    params = { pharmacyName, pharmacyPlz, pharmacyId }
  } else if (nameAndIdMatch) {
    const [, pharmacyName, pharmacyId] = nameAndIdMatch
    params = { pharmacyName, pharmacyId }
  } else if (idOnlyMatch) {
    const [pharmacyId] = idOnlyMatch
    params = { pharmacyId }
  }

  if (!params) {
    onShowLogs(() => console.error(`Invalid slug format: ${slug}`))
    return undefined
  }

  if (opts?.ensureRouteParamsType) {
    return ensurePharmacyParamsType(params)
  }

  return params
}

export const convertPharmacyParamsToSlug = ({ pharmacyId, pharmacyName, pharmacyPlz }: PartialPharmacyParams) => {
  const slugSegments = []

  if (pharmacyName) {
    slugSegments.push(pharmacyName)
  }

  if (pharmacyPlz) {
    slugSegments.push(pharmacyPlz)
  }

  slugSegments.push(pharmacyId)
  return slugSegments.join('-')
}

export const silentlyUpdateUrl = (url: URL) => {
  history.pushState({}, '', url.href)
}
